<template>
  <div class="app-container">
    <Container
      :title="t('route.updateSpecialPaymentRequestReimb')"
    >
      <template #main>
        <MultipleColumnsFrom
          ref="createFormRef"
          :formModel="formModel"
          :formStruct="formStruct"
          :formRules="formRules"
          :labelWidth="'180px'"
          :itemSpan="8"
          :itemOffset="1"
          hadSlot
          @submit="toSubmit"
          @reset="resetForm"
        >
          <!-- 在某个字段后添加按钮 -->
          <template #after-employeeNameCn>
            <el-button
              type="primary"
              @click="onSelect"
            >
              {{ t('common.select') }}
            </el-button>
          </template>
        </MultipleColumnsFrom>
      </template>
    </Container>
    <BaseDialog
      v-model="dialogVisible"
      :autoClose="false"
      width="40%"
      contentCenter
      destroy-on-close
      @confirm="onPurposeDialogConfirm"
      @close="onDialogClose"
    >
      <template #default>
        <el-select
          style="width: 85%"
          v-model="selectedEmployee"
          value-key="id"
          ref="selectIt"
          :placeholder="t('form.selectPlaceholder')"
          clearable
          filterable
        >
          <template #prefix>
            <i class="el-icon-search" />
          </template>
          <el-option
            v-for="(item,index) in employeeList"
            :key="index"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </template>
    </BaseDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import { useRoute, useRouter } from 'vue-router'
import { formatDate } from '@/utils'
import { getEmployeeAllList } from '@/apis/main-data/employee'
import dayjs from 'dayjs'
import { EmployeeInfoModel, EmployeeListParamModel } from '@/model/master-data/employeeModel'
import { UpdateReimbRequestModel } from '@/model/application-form/reimbursementRequest'
import { ElMessage } from 'element-plus'
import Container from '@/components/form/container/Container.vue'
import MultipleColumnsFrom from '@/components/form/common/MultipleColumnsFrom.vue'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import { getReimbRequestById, updateReimbRequest } from '@/apis/application-form/reimbursementRequest'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import { createPositiveNumberValidator } from '@/utils/validatorUtil'

export default defineComponent({
  components: {
    BaseDialog,
    Container,
    MultipleColumnsFrom
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const id = useRoute().params.id.toString()
    const positiveNumberValidator = createPositiveNumberValidator(t)
    const employeeStruct = [
      { prop: 'employeeNameCn', label: t('employee.nameCn'), component: 'el-input', type: 'text', model: '', readonly: true },
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'employeeNameEn', label: t('employee.nameEn'), component: 'el-input', type: 'text', model: '', readonly: true },
      { prop: 'amount', label: t('bank.amount'), component: 'el-input', type: 'text', model: '', decimalPlaces: 2, isPositive: true },
      { prop: 'bankAccount', label: t('bank.bankAccount'), component: 'el-input', type: 'text', model: '' },
      { prop: 'paymentDate', label: t('order.paymentDate'), component: 'el-date-picker', type: 'text', model: '' },
      { prop: 'bankCode', label: t('bank.bankNumber'), component: 'el-input', type: 'text', model: '' },
      { prop: 'comment', label: t('approvalForm.comment'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankName', label: t('bank.bankName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankCity', label: t('bank.bankCity'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isCompanyAccount',
        label: t('order.isCompanyAccount'),
        component: 'el-radio-group',
        type: 'boolean',
        model: null,
        fullRow: true,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      {
        prop: 'purpose',
        label: t('approvalForm.purpose'),
        component: 'el-input',
        type: 'textarea',
        model: '',
        rows: 5,
        maxlength: 100,
        showWordLimit: true
      }
    ]
    const employeeRules = {
      employeeNameCn: [{ required: true, message: t('employee.nameCnP'), trigger: 'change' }],
      companyCode: [{ required: true, message: t('company.companyCodeP'), trigger: 'change' }],
      employeeNameEn: [{ required: true, message: t('employee.nameEnP'), trigger: 'change' }],
      amount: [
        { required: true, message: t('bank.amountP'), trigger: 'blur' },
        { validator: positiveNumberValidator, trigger: 'blur' }
      ],
      bankAccount: [{ required: true, message: t('bank.bankAccountP'), trigger: 'blur' }],
      paymentDate: [{ required: true, message: t('order.paymentDateP'), trigger: 'blur' }],
      bankCode: [{ required: true, message: t('bank.bankNumberP'), trigger: 'blur' }],
      bankName: [{ required: true, message: t('bank.bankNameP'), trigger: 'blur' }],
      bankCity: [{ required: true, message: t('bank.bankCityP'), trigger: 'blur' }],
      isCompanyAccount: [{ required: true, message: t('order.isCompanyAccountP'), trigger: 'change' }],
      purpose: [{ required: true, message: t('approvalForm.purposeP'), trigger: 'blur' }]
    }
    const dataMap = reactive({
      formStruct: employeeStruct,
      formRules: employeeRules,
      formModel: createModel(employeeStruct) as UpdateReimbRequestModel,
      dialogVisible: false,
      selectedEmployee: null as EmployeeInfoModel | null,
      loadingEmployee: false,
      employeeList: [] as any,
      async getReimbById() {
        try {
          const poId = Number(id)
          const res = await getReimbRequestById(poId)
          const data = res?.data ?? null
          if (data) {
            dataMap.formModel.id = poId
            dataMap.formModel.employeeNameCn = data.employeeNameCn
            dataMap.formModel.employeeNameEn = data.employeeNameEn
            dataMap.formModel.companyCode = data.companyCode
            dataMap.formModel.bankAccount = data.bankAccount
            dataMap.formModel.bankCode = data.bankCode
            dataMap.formModel.bankName = data.bankName
            dataMap.formModel.amount = data.amount
            dataMap.formModel.paymentDate = formatDate(data.paymentDate, 'yyyy-MM-dd')
            dataMap.formModel.comment = data.comment
            dataMap.formModel.purpose = data.purpose
            dataMap.formModel.bankCity = data.bankCity
            dataMap.formModel.isCompanyAccount = data.isCompanyAccount
          }
        } catch (error) {
          ElMessage({
            message: 'id 格式错误;' + id,
            type: 'error',
            duration: 2000
          })
        }
      },
      async toSubmit(submitData: any) {
        const params: UpdateReimbRequestModel = {
          id: submitData.id,
          employeeNameCn: submitData.employeeNameCn,
          employeeNameEn: submitData.employeeNameEn,
          companyCode: submitData.companyCode,
          bankAccount: submitData.bankAccount,
          bankCode: submitData.bankCode,
          bankName: submitData.bankName,
          amount: submitData.amount,
          paymentDate: dayjs(submitData.paymentDate).format('YYYY-MM-DD HH:mm:ss'),
          comment: submitData.comment,
          purpose: submitData.purpose,
          bankCity: submitData.bankCity,
          isCompanyAccount: submitData.isCompanyAccount
        }

        try {
          const res = await updateReimbRequest(params)
          if (res?.data && res?.data > 0) {
            ElMessage({
              message: t('common.updateSuccess'),
              type: 'success',
              duration: 2000
            })
            await router.push({ name: 'ReimbursementRequestList', state: { refresh: true } })
          } else {
            ElMessage({
              message: res?.msg,
              type: 'error',
              duration: 2000
            })
          }
        } catch (error) {
          ElMessage({
            message: error,
            type: 'error',
            duration: 2000
          })
        }
      },
      async resetForm() {
        await dataMap.getReimbById()
      }
    })
    const getCompanyList = async() => {
      dataMap.formStruct[1].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.formStruct[1].options = optionList
      dataMap.formStruct[1].loading = false
    }
    const onSelect = async() => {
      dataMap.loadingEmployee = true
      const params: EmployeeListParamModel = {
        companyCode: null,
        employeeNumber: null,
        nameCn: null,
        nameEn: null,
        isDelete: false
      }
      const res = await getEmployeeAllList(params)
      dataMap.employeeList = Array<any>()
      if (res?.data) {
        res.data.forEach(employee => {
          if (dataMap.employeeList) {
            dataMap.employeeList.push({
              label: employee.nameCn + ', ' + employee.nameEn + ', ' + employee.employeeNumber,
              value: employee as EmployeeInfoModel
            })
          }
        })
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
      dataMap.loadingEmployee = false
      dataMap.dialogVisible = true
    }
    const onPurposeDialogConfirm = async() => {
      if (dataMap.selectedEmployee) {
        dataMap.formModel.employeeNameCn = dataMap.selectedEmployee.nameCn
        dataMap.formModel.employeeNameEn = dataMap.selectedEmployee.nameEn
        dataMap.formModel.companyCode = dataMap.selectedEmployee.companyCode
        dataMap.formModel.bankAccount = dataMap.selectedEmployee.bankAccount
        dataMap.formModel.bankCode = dataMap.selectedEmployee.bankCode
        dataMap.formModel.bankName = dataMap.selectedEmployee.bankName
        dataMap.formModel.bankCity = dataMap.selectedEmployee.bankCity
        dataMap.formModel.isCompanyAccount = dataMap.selectedEmployee.isCompanyAccount
        dataMap.dialogVisible = false
      } else {
        dataMap.formModel.employeeNameCn = ''
        dataMap.formModel.employeeNameEn = ''
        dataMap.formModel.companyCode = ''
        dataMap.formModel.bankAccount = ''
        dataMap.formModel.bankCode = ''
        dataMap.formModel.bankName = ''
        dataMap.formModel.bankCity = ''
        ElMessage({
          message: t('employee.employeeP'),
          type: 'error'
        })
      }
    }
    onMounted(() => {
      dataMap.getReimbById()
      getCompanyList()
    })
    return {
      t,
      onSelect,
      onPurposeDialogConfirm,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style scoped lang="scss">

</style>
